import React, { useContext, useMemo, useState } from "react";
import moment from "moment";
import { Row, Col, Badge, Select, Form, Table, DatePicker } from "antd";
import { Modal, message } from "antd";
import {
  dateInDetail,
  getFirstDate,
  getLastDateOfYear,
  getMySqlDate,
  isDateBetweenTwoDates,
} from "utils/Date";
import COLORS from "common/Colors";
import LeaveForm from "organisms/LeaveForm";
import { CALL_API } from "common/API";
import {
  getAllDepartmentList,
  getUserRoleId,
  getSelectedDepartmentId,
  getUserInfo,
  getClientid,
  getMenuList,
  getLeaveCategoryList,
  getUserList,
  getRoleList,
  getCountryList,
} from "redux/selectors";
import { filter, find, first, get, includes, map, uniqBy } from "lodash";
import { getAllSubordinates, STATUS_CODE } from "common/Constants";
import TabsList from "molecules/TabsList";
import {
  StyledDateCell,
  StyledHolidayBadge,
  StyledLeaveCount,
  StyledLeaveText,
} from "./StyledComponents";
import { CalendarContext } from ".";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import StaffLeaveIndicatorInCalendar from "./StaffLeaveIndicatorInCalendar";
import ReusableCalendar from "./ReusableCalendar";
import LeaveWeekCalendar from "pages/WeekCalendarView/LeaveWeekCalendar";
import { connect, useSelector } from "react-redux";

// ---------- helpers ----------
const toArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const norm = (v) => String(v ?? "").trim().toLowerCase();
const isEmptyDate = (d) =>
  d === "null" || d === null || d === "" || d === undefined;

// Does the leave [s, e] overlap the range [from, to] (inclusive, by day)?
const overlaps = (s, e, from, to) => {
  if (isEmptyDate(s) || isEmptyDate(e)) return false;
  const ls = moment(s).startOf("day");
  const le = moment(e).startOf("day");
  const f = moment(from).startOf("day");
  const t = moment(to).startOf("day");
  return ls.isSameOrBefore(t) && le.isSameOrAfter(f);
};

const STATUS_OPTIONS = [
  { label: "Waiting For Approval", value: "4" },
  { label: "Approved", value: "5" },
];

// ---------------------------------------------------------------------
// PERMISSION FIX: each calendar tab has its own menu entry carrying
// { view, create, edit, delete }:
//   Mine     -> "mycalendar"
//   My Team  -> "teamcalendar"
//   All      -> "allcalendar"
// ---------------------------------------------------------------------
const TAB_MENU_KEY = {
  Mine: "mycalendar",
  "My Team": "teamcalendar",
  All: "allcalendar",
};

const NO_PERMISSION_MESSAGE = "You don't have permission to perform this action.";

const resolvePermission = (menuItems, menuKey) => {
  const menu = (menuItems || []).find((item) => item?.key === menuKey);
  if (!menu || !menu.access) {
    return { view: false, create: false, edit: false, delete: false };
  }
  return {
    view: menu.permission?.view === true,
    create: menu.permission?.create === true,
    edit: menu.permission?.edit === true,
    delete: menu.permission?.delete === true,
  };
};

function LeaveCalendar({
  userRoleId,
  selectedDepartmentId,
  allDepartmentList,
  userInfo,
  ClientId,
  leaveCategoryList,
  menuList,
  userList,
  roleList,
  setTabId,
  tabId,
}) {
  const {
    tabBarExtraContent,
    miniCalendarCurrentDate,
    holidayList,
    allHolidayList,
  } = useContext(CalendarContext);

  const Menulist = menuList[userRoleId] || Object.values(menuList)[0];

  const tabMenus = Menulist.filter(
    (item) =>
      item.key === "mycalendar" ||
      item.key === "teamcalendar" ||
      item.key === "allcalendar"
  );

  const items = tabMenus
    .filter((data) => data.access === true)
    .map((data) => ({
      id: data.subtitle,
      label: data.subtitle,
      component: <></>,
    }));

  // PERMISSION FIX: permission of the tab that is currently open
  const permission = resolvePermission(
    Menulist,
    TAB_MENU_KEY[tabId] || TAB_MENU_KEY.Mine
  );

  const denyAction = () => message.warning(NO_PERMISSION_MESSAGE);

  // popup (All tab) state: status + date range, same as the screenshot
  const [isLeaveListModalVisible, toggleLeaveListModalVisible] =
    useState(false);
  const [leaveListFilterForHR, setLeaveListFilterForHR] = useState({
    status_id: "4",
    from: null,
    to: null,
  });

  const filteruser = userList?.filter(
    (item) => item?.userid === userInfo.userid
  );
  const currentUser = filteruser?.[0];

  const [displayLeaveModal, toggleLeaveModal] = useState(false);
  const [leaveDetails, setLeaveDetails] = useState({});
  const [showMyTeamLeaves, toggleMyTeamLeave] = useState(false);
  const countryList = useSelector(getCountryList);
  const [form] = Form.useForm();
  const [selectedManagingOffice, setSelectedManagingOffice] = useState(
    currentUser?.managingoffice
  );

  moment.updateLocale("en", {
    weekdaysMin: ["S", "M", "T", "W", "T", "F", "S"],
  });

  const subordinates = getAllSubordinates(userList, currentUser?.employeeid);

  const handleTabSelect = (key) => {
    const activeTab = items.find((item) => item.id === key);
    if (activeTab) {
      setTabId(activeTab.label);
    }
    if (key === 0) {
      toggleMyTeamLeave(!showMyTeamLeaves);
    }
  };

  const isSuperAdmin = userInfo?.roleId === "2";
  const isCountryAdmin = userInfo?.roleId === "3";

  // "All" must see every subordinate's leave, so no officesupervisorid filter.
  const officesupervisorid = tabId === "All" ? "" : currentUser?.employeeid;

  const payload = {
    status_id: "4,5",
    officesupervisorid,
    start_date: getFirstDate(miniCalendarCurrentDate),
    end_date: getLastDateOfYear(miniCalendarCurrentDate),
    show_my_team_leave: tabId === "My Team",
    ...(isSuperAdmin && { supeAdmin: true }),
    ...(isCountryAdmin && { countryAdmin: true }),
    country: "",
  };

  const { key, url } = queryKeys.getLeaveList(
    ClientId,
    JSON.stringify(payload)
  );

  const {
    data: leaveDataRes,
    isLoading: displayLoader,
    refetch: getLeaveList,
  } = useFetch(
    key,
    url,
    {
      enabled: !!ClientId,
    },
    {
      status_id: "4,5",
      officesupervisorid,
      start_date: getFirstDate(miniCalendarCurrentDate),
      end_date: getLastDateOfYear(miniCalendarCurrentDate),
      show_my_team_leave: tabId === "My Team",
      country:
        tabId === "My Team"
          ? ""
          : selectedManagingOffice ?? currentUser?.managingoffice,
    }
  );

  const leaveList = leaveDataRes?.response?.leaves?.filter(
    (val) => val?.userid === get(userInfo, "userid", "")
  );

  // ---------- My Team: subordinates + countryAdmin ----------
  const allowedTeamCountries = useMemo(() => {
    const raw = [
      ...(isCountryAdmin ? toArray(currentUser?.managingoffice) : []),
    ].map(norm);

    const set = new Set(raw);

    (countryList || []).forEach((c) => {
      const name = norm(c?.countryname);
      const code = norm(c?.countrycode);
      if (set.has(name) || set.has(code)) {
        if (name) set.add(name);
        if (code) set.add(code);
      }
    });

    return set;
  }, [currentUser, isCountryAdmin, countryList]);

  const myTeamLeaveList = useMemo(() => {
    const teamLeaves = leaveDataRes?.response?.teamLeaves ?? [];
    return teamLeaves.filter(
      (val) =>
        subordinates.includes(val?.employeeid) ||
        allowedTeamCountries.has(norm(val?.country))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaveDataRes, allowedTeamCountries, userList, currentUser?.employeeid]);

  const allLeaveList = leaveDataRes?.response?.leaves;

  // ---------- "All" tab popup list: built from the SAME data as the badges ----------
  const popupLeaves = useMemo(() => {
    const { status_id, from, to } = leaveListFilterForHR;
    if (!from || !to) return [];

    return (allLeaveList ?? [])
      .filter((l) => {
        if (status_id === "4") {
          // pending: new application, or approved leave with a cancel request
          return (
            (l?.status_id === "4" &&
              overlaps(l.start_date, l.end_date, from, to)) ||
            (l?.status_id === "5" &&
              l?.having_cancel_request === "1" &&
              overlaps(l.new_start_date, l.new_end_date, from, to))
          );
        }
        if (status_id === "5") {
          return (
            l?.status_id === "5" &&
            (l?.having_cancel_request === "0" ||
              l?.having_cancel_request === "" ||
              l?.having_cancel_request === undefined) &&
            overlaps(l.start_date, l.end_date, from, to)
          );
        }
        return false;
      })
      .map((l) => {
        const useNew =
          l?.status_id === "5" && l?.having_cancel_request === "1";
        return {
          key: l.user_leave_id,
          name: (l.fullname ?? "").trim(),
          start_date: useNew ? l.new_start_date : l.start_date,
          end_date: useNew ? l.new_end_date : l.end_date,
          leave_type: (l.leave_category_name ?? "").trim(),
          days:
            (useNew && !isEmptyDate(l.new_number_of_days)
              ? l.new_number_of_days
              : l.number_of_days ?? l.no_of_days) ?? "",
        };
      });
  }, [allLeaveList, leaveListFilterForHR]);

  const popupColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
      render: (d) => (d ? moment(d).format("YYYY-MM-DD") : ""),
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
      render: (d) => (d ? moment(d).format("YYYY-MM-DD") : ""),
    },
    { title: "Leave Type", dataIndex: "leave_type", key: "leave_type" },
    { title: "Days", dataIndex: "days", key: "days" },
  ];

  const openLeavePopup = (cellDate, status_id) => {
    setLeaveListFilterForHR({
      status_id,
      from: moment(cellDate),
      to: moment(cellDate),
    });
    toggleLeaveListModalVisible(true);
  };

  const addLeave = (event, cellDate) => {
    if (!permission.create) return; // PERMISSION FIX
    if (
      userRoleId != 1 &&
      event.target.className === "ant-picker-calendar-date-content"
    ) {
      const isPastDate =
        moment(getMySqlDate(cellDate)) < moment(getMySqlDate("2022-08-01"));
      if (!isPastDate) {
        toggleLeaveModal(true);
        setLeaveDetails({
          start_date: cellDate,
          end_date: cellDate,
        });
      }
    }
  };

  const handleFormChange = (value) => {
    setSelectedManagingOffice(value);
  };

  const AllTabID = tabId === "All" && !!items?.find((i) => i.id === "All");
  const MYTabID = tabId === "Mine" && !!items?.find((i) => i.id === "Mine");
  const MYTEAMTabID =
    tabId === "My Team" && !!items?.find((i) => i.id === "My Team");

  const leaveCoversDate = (cellDate, l) =>
    includes(["4", "5"], l.status_id) &&
    isDateBetweenTwoDates(
      cellDate,
      isEmptyDate(l.new_start_date) ? l.start_date : l.new_start_date,
      isEmptyDate(l.new_end_date) ? l.end_date : l.new_end_date
    );

  // PERMISSION FIX: opens the leave form to edit an existing leave
  const openEditLeaveForm = (details) => {
    if (!permission.edit) {
      denyAction();
      return;
    }
    setLeaveDetails(details);
    toggleLeaveModal(true);
  };

  const dateFullCellRender = (cellDate) => {
    const { date: day, day: Days } = dateInDetail(cellDate);

    const leave = filter(leaveList, (l) => leaveCoversDate(cellDate, l));

    const teamLeave = MYTEAMTabID
      ? filter(myTeamLeaveList, (l) => leaveCoversDate(cellDate, l))
      : [];

    const isHoliday = find(
      MYTabID ? holidayList : allHolidayList,
      ({ start_date, end_date }) =>
        isDateBetweenTwoDates(cellDate, start_date, end_date)
    );
    const leaveColor = get(isHoliday, "color", "");

    const isLeaveShownOnDay = (l, ignoreHoliday) => {
      const cat = leaveCategoryList?.find(
        (c) => c.leave_category_id === l?.leave_category_id
      );
      const weekendOk = cat?.weekend === "Yes" || Days !== "Sunday";
      const saturdayOk = cat?.isSaturday === "Yes" || Days !== "Saturday";
      const holidayOk = cat?.holiday === "Yes" || ignoreHoliday || !isHoliday;
      const statusOk =
        l?.status_id === "4" ||
        l?.status_id === "5" ||
        l?.having_cancel_request === "1";
      return statusOk && weekendOk && saturdayOk && holidayOk;
    };

    const firstMyLeave = first(leave);
    const leaveType = firstMyLeave
      ? isLeaveShownOnDay(firstMyLeave, false)
        ? firstMyLeave.status_id === "5" &&
          firstMyLeave.having_cancel_request !== "1"
          ? "has-approved-leaves"
          : "has-pending-leaves"
        : ""
      : "";

    const visibleTeamLeave = filter(teamLeave, (l) => isLeaveShownOnDay(l, true));

    const hrPendingLeaves = filter(
      allLeaveList,
      ({
        start_date,
        end_date,
        status_id,
        having_cancel_request,
        new_start_date,
        new_end_date,
      }) =>
        (status_id === "4" &&
          isDateBetweenTwoDates(cellDate, start_date, end_date)) ||
        (status_id === "5" &&
          having_cancel_request === "1" &&
          isDateBetweenTwoDates(cellDate, new_start_date, new_end_date))
    ).length;

    const hrApprovedLeaves = filter(
      allLeaveList,
      ({ start_date, end_date, status_id, having_cancel_request }) =>
        status_id === "5" &&
        (having_cancel_request === "0" || having_cancel_request === "") &&
        isDateBetweenTwoDates(cellDate, start_date, end_date)
    ).length;

    return (
      <>
        {MYTabID && (
          <StyledDateCell
            onClick={(event) =>
              permission.create ? addLeave(event, cellDate) : ""
            }
          >
            <span className={`day ${leaveType}`}>
              {leaveColor && (
                <StyledHolidayBadge
                  status="success"
                  size="small"
                  color={leaveColor}
                >
                  H
                </StyledHolidayBadge>
              )}
              {leaveType &&
                map(leave, (details) => (
                  <StaffLeaveIndicatorInCalendar
                    key={details.user_leave_id}
                    icon="team"
                    leaveDetails={details}
                    isStaff={true}
                    day={day}
                    permission={permission}
                    openEditLeaveModal={(d) => {
                      cancelLeaveRequest(d);
                    }}
                    EditLeaveModal={(d) => {
                      cancelLeave(d);
                    }}
                  >
                    {day}
                  </StaffLeaveIndicatorInCalendar>
                ))}
              {!leaveType && day}
            </span>
            <div className="ant-picker-calendar-date-content"></div>
          </StyledDateCell>
        )}

        {MYTEAMTabID && (
          <StyledDateCell>
            <b>{day}</b>
            {leaveColor && (
              <StyledHolidayBadge
                status="success"
                size="small"
                color={leaveColor}
              >
                H
              </StyledHolidayBadge>
            )}
            <div className="ant-picker-calendar-date-content">
              {map(visibleTeamLeave, (details) => (
                <StaffLeaveIndicatorInCalendar
                  key={details.user_leave_id}
                  icon="team"
                  leaveDetails={details}
                  isStaff={false}
                  reloadList={getLeaveList}
                  permission={permission}
                  openEditLeaveModal={() => openEditLeaveForm(details)}
                  EditLeaveModal={() => openEditLeaveForm(details)}
                  displayLeaveDetail={AllTabID}
                  Days={Days}
                />
              ))}
            </div>
          </StyledDateCell>
        )}

        {AllTabID && (
          <StyledDateCell>
            <b>{day}</b>
            {leaveColor && (
              <StyledHolidayBadge
                status="success"
                size="small"
                color={leaveColor}
              >
                H
              </StyledHolidayBadge>
            )}
            <div className="ant-picker-calendar-date-content">
              <div className="hr-leave-container">
                <Badge
                  count={hrPendingLeaves}
                  className="hr-has-pending-leaves"
                  onClick={() => openLeavePopup(cellDate, "4")}
                />
                <Badge
                  count={hrApprovedLeaves}
                  className="hr-has-approved-leaves"
                  onClick={() => openLeavePopup(cellDate, "5")}
                />
              </div>
            </div>
          </StyledDateCell>
        )}
      </>
    );
  };

  const confirmCancelLeave = (details, tag) => {
    // PERMISSION FIX: cancelling a leave application needs delete access
    if (!permission.delete) {
      denyAction();
      return;
    }
    const {
      user_leave_id,
      start_date: leave_start_date,
      end_date: leave_end_date,
    } = details;
    Modal.confirm({
      icon: "",
      content: "Are you sure you want to cancel your leave application?",
      okText: "Yes",
      cancelText: "No",
      onOk: () =>
        new Promise(async (resolve) => {
          const { code } = await CALL_API(
            `approve-reject-cancel-leave/${ClientId}`,
            "post",
            {
              user_leave_id,
              status_id: "9",
              is_cancel_request: false,
              start_date: getMySqlDate(leave_start_date),
              end_date: getMySqlDate(leave_end_date),
            }
          );
          if (code === STATUS_CODE.SOMETHING_WENT_WRONG) {
            message.error(`Oops!! something went wrong.`);
          } else if (code === STATUS_CODE.INVALID_PAYLOAD) {
            message.error(`Invalid payload. Please enter correct data.`);
          } else if (code === STATUS_CODE.RECORD_EXIST) {
            message.error(`Leave aleady exist.`);
          } else if (code === STATUS_CODE.SUCCESS) {
            message.success(`Your leave application has been cancelled.`);
            getLeaveList();
          }
          resolve();
        }).catch((err) => console.log(`[${tag}] Error -->`, err)),
    });
  };

  const cancelLeaveRequest = (details) => {
    if (userRoleId !== 1 && details.status_id !== "4") {
      confirmCancelLeave(details, "cancelLeaveRequest");
    } else {
      openEditLeaveForm(details); // needs edit access
    }
  };

  const cancelLeave = (details) => {
    if (userRoleId !== 1 && details.status_id === "4") {
      confirmCancelLeave(details, "cancelLeave");
    } else {
      openEditLeaveForm(details); // needs edit access
    }
  };

  const leaveCalendarContent = (
    <>
      <Row gutter={20}>
        <Col span={4}>
          <StyledLeaveCount color={COLORS.PENDING_LEAVE} />
        </Col>
        <Col span={20}>
          <StyledLeaveText>
            Pending Leaves{includes([1, 4], userRoleId) ? "" : " count"}
          </StyledLeaveText>
        </Col>
      </Row>
      <Row gutter={20}>
        <Col span={4}>
          <StyledLeaveCount color={COLORS.APPROVED_LEAVE} />
        </Col>
        <Col span={20}>
          <StyledLeaveText>
            Approved Leaves{includes([1, 4], userRoleId) ? "" : " count"}
          </StyledLeaveText>
        </Col>
      </Row>
    </>
  );

  const uniquemanagingOffice = uniqBy(
    countryList?.map((item) => ({
      label: item?.countryname,
      value: item?.countrycode,
    })),
    "value"
  );

  return (
    <>
      <TabsList
        items={items}
        handleTabSelect={handleTabSelect}
        tabBarExtraContent={tabBarExtraContent}
        tabId={tabId}
      />
      {MYTabID && (
        <LeaveWeekCalendar holidayList={holidayList} permission={permission} />
      )}
      {!MYTabID && (
        <>
          {AllTabID && currentUser?.roleid?.includes("2") && (
            <Row justify="end">
              <Col>
                <Select
                  showSearch
                  options={uniquemanagingOffice}
                  placeholder="Select Country"
                  labelInValue
                  style={{ width: 200 }}
                  onChange={(option) => {
                    form.setFieldsValue({ managingoffice: option });
                    handleFormChange(option.label);
                  }}
                />
              </Col>
            </Row>
          )}

          <ReusableCalendar
            dateFullCellRender={dateFullCellRender}
            isCalendarLoading={displayLoader}
            calendarTitle={MYTabID ? "My Calendar" : "Calendar"}
            leftPanelContent={leaveCalendarContent}
          />
        </>
      )}

      {/* PERMISSION FIX: the leave form only exists for users who can create or edit */}
      {(permission.create || permission.edit) && (
        <LeaveForm
          isModalVisible={displayLeaveModal}
          closeModal={() => toggleLeaveModal(false)}
          leaveDetails={leaveDetails}
          getLeaveList={getLeaveList}
          showApproverLeaveBalance={MYTEAMTabID}
        />
      )}

      {/* Leave list popup for the "All" tab - uses the same data as the badges */}
      <Modal
        open={isLeaveListModalVisible}
        visible={isLeaveListModalVisible}
        onCancel={() => toggleLeaveListModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Row gutter={12} style={{ marginBottom: 16, marginTop: 24 }}>
          <Col span={8}>
            <Select
              style={{ width: "100%" }}
              options={STATUS_OPTIONS}
              value={leaveListFilterForHR.status_id}
              onChange={(v) =>
                setLeaveListFilterForHR((prev) => ({ ...prev, status_id: v }))
              }
            />
          </Col>
          <Col span={8}>
            <DatePicker
              style={{ width: "100%" }}
              allowClear={false}
              value={leaveListFilterForHR.from}
              onChange={(d) =>
                setLeaveListFilterForHR((prev) => ({
                  ...prev,
                  from: d,
                  to: prev.to && d && d.isAfter(prev.to, "day") ? d : prev.to,
                }))
              }
            />
          </Col>
          <Col span={8}>
            <DatePicker
              style={{ width: "100%" }}
              allowClear={false}
              value={leaveListFilterForHR.to}
              disabledDate={(d) =>
                leaveListFilterForHR.from &&
                d.isBefore(leaveListFilterForHR.from, "day")
              }
              onChange={(d) =>
                setLeaveListFilterForHR((prev) => ({ ...prev, to: d }))
              }
            />
          </Col>
        </Row>
        <Table
          size="small"
          columns={popupColumns}
          dataSource={popupLeaves}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          loading={displayLoader}
        />
      </Modal>
    </>
  );
}

const mapStateToProps = (state) => ({
  userRoleId: getUserRoleId(state),
  allDepartmentList: getAllDepartmentList(state),
  selectedDepartmentId: getSelectedDepartmentId(state),
  userInfo: getUserInfo(state),
  ClientId: getClientid(state),
  menuList: getMenuList(state),
  leaveCategoryList: getLeaveCategoryList(state),
  userList: getUserList(state),
  roleList: getRoleList(state),
});

export default connect(mapStateToProps, null)(LeaveCalendar);
